// Typed param shapes for the 9 pricing rule types. These MIRROR the backend
// wire JSON exactly (internal/domain/pricing/params.go) — the API stores and
// returns this JSONB verbatim, so the editor edits these shapes directly (no
// codec transform). Percentages are percent units (10 = +10%, -35 = -35%).
// Money fields (`abs_minor`, `set_rate_minor`) are int64 MINOR units.
import type { PricingRuleType } from '@/lib/pricing';

export { RULE_TYPE_LABELS } from '@/lib/pricing';
export type { PricingRuleType };

// Weekday keys in display order (backend accepts mon..sun).
export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type Weekday = (typeof WEEKDAYS)[number];
export const WEEKDAY_LABELS: Record<Weekday, string> = {
  mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun',
};

export const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;
export const MONTH_LABELS: Record<number, string> = {
  1: 'Jan', 2: 'Feb', 3: 'Mar', 4: 'Apr', 5: 'May', 6: 'Jun',
  7: 'Jul', 8: 'Aug', 9: 'Sep', 10: 'Oct', 11: 'Nov', 12: 'Dec',
};

// Shared calendar adjustment: an optional pct AND/OR an optional absolute minor.
export interface CalAdj {
  pct?: number;
  abs_minor?: number;
}

export interface MonthParams {
  months: Record<string, CalAdj>; // key "1".."12"
}

export interface SeasonEntry {
  from: string; // "MM-DD"
  to: string; // "MM-DD"
  pct?: number;
  abs_minor?: number;
  min_stay?: number;
  // Price band for these dates (OH-142). Each side is independent and absent
  // means "keep the apartment's own bound" — which is why an untouched field
  // must stay out of the params rather than go in as 0.
  min_price_minor?: number;
  max_price_minor?: number;
}
export interface SeasonalParams {
  seasons: SeasonEntry[];
}

export interface DayOfWeekParams {
  days: Record<string, CalAdj>; // key "mon".."sun"
  months?: number[]; // optional 1..12 filter
}

export interface DateSpecificEntry {
  from: string; // "YYYY-MM-DD"
  to: string; // "YYYY-MM-DD"
  pct?: number;
  abs_minor?: number;
  set_rate_minor?: number; // hard override; mutually exclusive with pct/abs_minor
  min_stay?: number;
}
export interface DateSpecificParams {
  entries: DateSpecificEntry[];
}

export interface MinNightsParams {
  by_dow: Record<string, number>; // key "mon".."sun" → min nights (>=1)
}

export interface RestrictionEntry {
  from?: string; // "YYYY-MM-DD"
  to?: string; // "YYYY-MM-DD"
  days?: string[]; // weekday keys
  cta: boolean;
  ctd: boolean;
  stop_sell: boolean;
}
export interface BookingRestrictionParams {
  entries: RestrictionEntry[];
}

export interface GapNightParams {
  max_gap_nights: number; // >= 1
  pct?: number;
  abs_minor?: number;
  set_min_stay_to_gap?: boolean;
}

// last_minute / advanced_booking: tiered lead-time. min_stay is explicit null
// when unset (the backend uses `*int` without omitempty).
export interface LastMinuteTier {
  within_days: number; // >= 1
  pct: number;
  min_stay: number | null;
}
export interface LastMinuteParams {
  tiers: LastMinuteTier[];
}
export interface AdvancedBookingTier {
  beyond_days: number; // >= 1
  pct: number;
  min_stay: number | null;
}
export interface AdvancedBookingParams {
  tiers: AdvancedBookingTier[];
}

export type ExtraGuestBasis = 'per_night' | 'per_stay';

// extra_guest: the surcharge for adults beyond the apartment's base occupancy.
// The base is NOT here — it is the apartment's own default occupancy, so one
// strategy serves a studio and a three-bedroom without a per-apartment rate.
export interface ExtraGuestParams {
  amount_minor: number;
  basis: ExtraGuestBasis;
}

// Every per-type rule form is a controlled editor of its params shape.
export interface RuleFormProps<T> {
  params: T;
  onChange: (next: T) => void;
}

// Map a rule type to its params shape (compile-time aid for form components).
export interface RuleParamsByType {
  month: MonthParams;
  seasonal: SeasonalParams;
  day_of_week: DayOfWeekParams;
  date_specific: DateSpecificParams;
  min_nights: MinNightsParams;
  booking_restriction: BookingRestrictionParams;
  gap_night: GapNightParams;
  last_minute: LastMinuteParams;
  advanced_booking: AdvancedBookingParams;
  extra_guest: ExtraGuestParams;
}

// defaultParams returns the starting params doc for a freshly-added rule.
// All are minimally-valid against the backend `ValidateRuleParams` (non-empty
// entries/tiers/days where required; max_gap_nights >= 1) EXCEPT `date_specific`,
// which seeds empty `from`/`to` on purpose: there is no sensible default date
// range to apply silently, so the operator must pick the dates before saving
// (a premature save surfaces the backend 400 as a readable error).
export function defaultParams(type: PricingRuleType): Record<string, unknown> {
  switch (type) {
    case 'month':
      return { months: {} } satisfies MonthParams;
    case 'seasonal':
      return { seasons: [{ from: '06-01', to: '08-31', pct: 0 }] } satisfies SeasonalParams;
    case 'day_of_week':
      return { days: { fri: { pct: 0 }, sat: { pct: 0 } } } satisfies DayOfWeekParams;
    case 'date_specific':
      return { entries: [{ from: '', to: '', pct: 0 }] } satisfies DateSpecificParams;
    case 'min_nights':
      return { by_dow: { fri: 2, sat: 2 } } satisfies MinNightsParams;
    case 'booking_restriction':
      return { entries: [{ cta: false, ctd: false, stop_sell: false }] } satisfies BookingRestrictionParams;
    case 'gap_night':
      return { max_gap_nights: 1, set_min_stay_to_gap: true } satisfies GapNightParams;
    case 'last_minute':
      return { tiers: [{ within_days: 7, pct: 0, min_stay: null }] } satisfies LastMinuteParams;
    case 'advanced_booking':
      return { tiers: [{ beyond_days: 60, pct: 0, min_stay: null }] } satisfies AdvancedBookingParams;
    case 'extra_guest':
      return { amount_minor: 0, basis: 'per_night' } satisfies ExtraGuestParams;
    default: {
      // Exhaustiveness guard.
      const _never: never = type;
      return _never;
    }
  }
}

// ─── money helpers (minor ↔ major-unit string), shared by money-bearing forms ──

// minorToMajorStr renders an int64 minor amount (or undefined) as a major-unit
// string ("" when absent), for an editable input.
export function minorToMajorStr(minor: number | undefined | null): string {
  if (minor == null) return '';
  return (minor / 100).toFixed(2);
}

// majorStrToMinorOpt parses a major-unit string to minor units. "" → undefined
// (field absent). Returns the symbol `INVALID` for a non-numeric non-empty
// value so callers can flag it. Negatives are allowed (abs_minor can be
// negative); callers that forbid negatives check separately.
export const INVALID = Symbol('invalid-number');
export function majorStrToMinorOpt(v: string): number | undefined | typeof INVALID {
  const t = v.trim();
  if (t === '') return undefined;
  const n = Number.parseFloat(t);
  if (!Number.isFinite(n)) return INVALID;
  return Math.round(n * 100);
}

// pctStrToNumOpt parses a percent string. "" → undefined; bad → INVALID.
export function pctStrToNumOpt(v: string): number | undefined | typeof INVALID {
  const t = v.trim();
  if (t === '') return undefined;
  const n = Number.parseFloat(t);
  if (!Number.isFinite(n)) return INVALID;
  return n;
}

// intStrOpt parses an integer string. "" → undefined; bad → INVALID.
export function intStrOpt(v: string): number | undefined | typeof INVALID {
  const t = v.trim();
  if (t === '') return undefined;
  const n = Number.parseInt(t, 10);
  if (!Number.isFinite(n)) return INVALID;
  return n;
}
